import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/DateInput";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ledgerApi, mydataError } from "@/api";
import { useLang, fmtDate } from "@/i18n";
import { PeriodFilter, yearStart, today, money } from "@/components/ledger/PeriodFilter";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const SOURCES = ["document", "payment", "settlement", "payslip", "payslip_payment", "manual"];
const newLine = () => ({ account_code: "", debit: "", credit: "" });

export const Journal = () => {
  const { t, lang } = useLang();
  const [rows, setRows] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [from, setFrom] = useState(yearStart());
  const [to, setTo] = useState(today());
  const [source, setSource] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date: today(), description: "", lines: [newLine(), newLine()] });
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(() => {
    ledgerApi.journal({ date_from: from || undefined, date_to: to || undefined, source }).then(setRows).catch(() => toast.error(t("error")));
  }, [from, to, source, t]);
  useEffect(load, [load]);
  useEffect(() => { ledgerApi.accounts().then(setAccounts); }, []);

  const setLine = (i, k, v) => setForm((f) => ({ ...f, lines: f.lines.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)) }));
  const totals = form.lines.reduce((a, l) => ({ d: a.d + (Number(l.debit) || 0), c: a.c + (Number(l.credit) || 0) }), { d: 0, c: 0 });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.description.trim()) return toast.error(`${t("description")}: ${t("required")}`);
    try {
      await ledgerApi.createEntry({ ...form, lines: form.lines.filter((l) => l.account_code).map((l) => ({ account_code: l.account_code, debit: Number(l.debit) || 0, credit: Number(l.credit) || 0 })) });
      toast.success(t("entry_saved")); setOpen(false);
      if (form.date < from) setFrom(form.date);
      if (form.date > to) setTo(form.date);
      setForm({ date: today(), description: "", lines: [newLine(), newLine()] }); load();
    } catch (err) { toast.error(mydataError(err, t)); }
  };
  const remove = async () => {
    try { await ledgerApi.deleteEntry(deleting.id); toast.success(t("entry_deleted")); load(); } catch { toast.error(t("error")); } finally { setDeleting(null); }
  };

  return (
    <div className="space-y-4" data-testid="journal-tab">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-3">
          <PeriodFilter from={from} to={to} setFrom={setFrom} setTo={setTo} prefix="jr" />
          <Select value={source} onValueChange={setSource}>
            <SelectTrigger className="w-52" data-testid="jr-source-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">{t("all_sources")}</SelectItem>{SOURCES.map((s) => <SelectItem key={s} value={s}>{t(`src_${s}`)}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <a href={ledgerApi.exportUrl({ date_from: from, date_to: to, source })} download data-testid="jr-export-excel-link"
            className="h-10 px-4 inline-flex items-center gap-2 rounded-md border border-slate-200 dark:border-slate-700 text-sm font-medium hover:bg-slate-100 dark:hover:bg-slate-800">
            <FileSpreadsheet size={15} />{t("export_excel")}
          </a>
          <Button onClick={() => setOpen(true)} className="bg-blue-800 hover:bg-blue-700" data-testid="create-entry-button"><Plus size={16} className="mr-1" />{t("new_entry")}</Button>
        </div>
      </div>
      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.id} className="erp-card p-4" data-testid={`journal-entry-${r.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2 text-sm mb-2">
              <div><span className="font-mono text-slate-500 mr-3">{fmtDate(r.date, lang)}</span><span className="font-medium">{r.description}</span>
                <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-400">{t(`src_${r.source.type}`)}</span></div>
              {r.source.type === "manual" && <button onClick={() => setDeleting(r)} data-testid={`journal-delete-${r.id}`} className="h-7 w-7 inline-flex items-center justify-center rounded hover:bg-rose-100 dark:hover:bg-rose-950 text-rose-600"><Trash2 size={14} /></button>}
            </div>
            <table className="w-full text-xs font-mono">
              <tbody>
                {r.lines.map((l, i) => (
                  <tr key={i} className="border-t border-slate-100 dark:border-slate-800">
                    <td className="py-1 w-32" style={{ paddingLeft: l.credit ? 24 : 0 }}>{l.account_code}</td>
                    <td className="py-1 font-body text-slate-600 dark:text-slate-300">{l.account_name}</td>
                    <td className="py-1 text-right w-28">{money(l.debit)}</td>
                    <td className="py-1 text-right w-28">{money(l.credit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
        {rows.length === 0 && <div className="erp-card px-4 py-10 text-center text-sm text-slate-500">{t("no_results")}</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl" data-testid="entry-dialog">
          <DialogHeader><DialogTitle className="font-heading">{t("new_entry")}</DialogTitle><DialogDescription className="sr-only">{t("journal")}</DialogDescription></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <DateInput value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="font-mono" data-testid="entry-date-input" />
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder={t("description")} className="col-span-2" data-testid="entry-description-input" />
            </div>
            <div className="space-y-2">
              {form.lines.map((l, i) => (
                <div key={i} className="grid grid-cols-12 gap-2">
                  <div className="col-span-7">
                    <Select value={l.account_code} onValueChange={(v) => setLine(i, "account_code", v)}>
                      <SelectTrigger className="font-mono text-xs" data-testid={`entry-line-account-${i}`}><SelectValue placeholder={t("select_account")} /></SelectTrigger>
                      <SelectContent className="max-h-72">{accounts.map((a) => <SelectItem key={a.code} value={a.code}>{a.code} · {a.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <Input type="number" step="0.01" min="0" value={l.debit} onChange={(e) => setLine(i, "debit", e.target.value)} placeholder={t("debit")} className="col-span-2 font-mono" data-testid={`entry-line-debit-${i}`} />
                  <Input type="number" step="0.01" min="0" value={l.credit} onChange={(e) => setLine(i, "credit", e.target.value)} placeholder={t("credit")} className="col-span-2 font-mono" data-testid={`entry-line-credit-${i}`} />
                  <button type="button" onClick={() => setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }))} disabled={form.lines.length <= 2} className="col-span-1 text-rose-600 disabled:opacity-30"><Trash2 size={14} /></button>
                </div>
              ))}
              <div className="flex justify-between items-center text-xs font-mono">
                <Button type="button" size="sm" variant="outline" onClick={() => setForm((f) => ({ ...f, lines: [...f.lines, newLine()] }))} data-testid="entry-add-line-button"><Plus size={12} className="mr-1" />{t("add_line")}</Button>
                <span className={totals.d.toFixed(2) === totals.c.toFixed(2) ? "text-emerald-700" : "text-rose-600"} data-testid="entry-totals">{t("debit")} {totals.d.toFixed(2)} · {t("credit")} {totals.c.toFixed(2)}</span>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
              <Button type="submit" className="bg-blue-800 hover:bg-blue-700" data-testid="entry-submit-button">{t("save")}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <ConfirmDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)} message={t("confirm_delete_entry")} onConfirm={remove} />
    </div>
  );
};
